import { useEffect, useState } from "react";
import { AlertCircle, ArrowRight, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { useLocation } from "react-router-dom";

import Logo from "@/components/shared/Logo";
import { Button } from "@/components/ui/button";
import {
  openOnboardingCodeLink,
  pollOnboardingCodeLink,
  readOnboardingCodeLinkToken,
} from "@/lib/onboarding-code-link-api";
import { useI18n } from "@/lib/i18n";

const POLL_INTERVAL_MS = 1500;

function readPairingCode(payload) {
  return String(payload?.code || payload?.pairingCode || "").trim();
}

export default function LinkCode() {
  const { tx } = useI18n();
  const location = useLocation();
  const token = readOnboardingCodeLinkToken(location.search);
  const [session, setSession] = useState(null);
  const [pairingCode, setPairingCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isOpening, setIsOpening] = useState(true);
  const [waitingForConfirmation, setWaitingForConfirmation] = useState(false);

  const status = String(session?.status || "").toLowerCase();
  const isLinked = status === "linked";
  const isExpired = status === "expired";
  const isFailed = status === "failed";
  const selectedPhone = session?.maskedPhoneNumber || tx("your WhatsApp number");
  const finishSetupUrl = String(session?.finishSetupUrl || "").trim();
  const isPreparing = isOpening || (!pairingCode && !errorMessage && !isExpired && !isFailed && !isLinked);

  useEffect(() => {
    let cancelled = false;

    async function openCodeLink() {
      if (!token) {
        setErrorMessage("This code link is missing. Reply to Min Support for help.");
        setIsOpening(false);
        return;
      }
      try {
        setErrorMessage("");
        setIsOpening(true);
        const payload = await openOnboardingCodeLink(token);
        if (cancelled) {
          return;
        }
        setSession(payload);
        const nextCode = readPairingCode(payload);
        if (nextCode) {
          setPairingCode(nextCode);
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(error?.message || "Could not open this WhatsApp code.");
        }
      } finally {
        if (!cancelled) {
          setIsOpening(false);
        }
      }
    }

    void openCodeLink();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!token || errorMessage || isLinked || isExpired || isFailed) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      void pollOnboardingCodeLink(token)
        .then((payload) => {
          setSession(payload);
          const nextCode = readPairingCode(payload);
          if (nextCode) {
            setPairingCode(nextCode);
          }
          if (String(payload?.status || "").toLowerCase() === "linked") {
            setWaitingForConfirmation(false);
          }
        })
        .catch((error) => {
          setErrorMessage(error?.message || "Could not check link status.");
        });
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [errorMessage, isExpired, isFailed, isLinked, token]);

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background">
      <nav className="flex items-center justify-between px-5 py-4">
        <Logo size="sm" />
      </nav>

      <main className="flex flex-1 items-center justify-center px-5 pb-16">
        <section className="w-full max-w-sm text-center">
          <div className="mb-6">
            <h1 className="font-display text-2xl font-bold">
              {tx(errorMessage
                ? "Code needs help"
                : isLinked
                  ? "Min is linked"
                  : isFailed
                    ? "Code needs help"
                    : isExpired
                      ? "Code expired"
                      : pairingCode
                        ? "Link WhatsApp"
                        : "Preparing your code")}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {tx(errorMessage
                ? "Reply to Min Support and we will help."
                : isLinked
                  ? "Send yourself a voice note to test Min."
                  : isFailed
                    ? "Reply to Min Support and we will help."
                    : isExpired
                      ? "Reply to Min Support and we will send a fresh link."
                      : pairingCode
                        ? "Use this code inside WhatsApp."
                        : "This can take up to 60 seconds.")}
            </p>
          </div>

          <div className="rounded-3xl border border-border bg-card p-6">
            {errorMessage || isExpired || isFailed ? (
              <div className="flex flex-col items-center gap-4">
                <AlertCircle className="h-12 w-12 text-destructive" />
                <p className="text-sm text-muted-foreground">
                  {tx(errorMessage || (isFailed ? "This WhatsApp session could not start." : "This code link is no longer active."))}
                </p>
              </div>
            ) : isLinked ? (
              <div className="flex flex-col items-center gap-4">
                <CheckCircle2 className="h-12 w-12 text-emerald-600" />
                <p className="text-sm text-muted-foreground">{tx("You can close this page.")}</p>
                {finishSetupUrl ? (
                  <Button asChild className="h-12 w-full rounded-xl">
                    <a href={finishSetupUrl}>
                      {tx("Finish setup")}
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </Button>
                ) : null}
              </div>
            ) : isPreparing ? (
              <div className="flex min-h-[220px] flex-col items-center justify-center gap-4">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <div>
                  <p className="font-semibold text-foreground">{tx("Getting your code")}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{selectedPhone}</p>
                </div>
              </div>
            ) : waitingForConfirmation ? (
              <div className="flex min-h-[220px] flex-col items-center justify-center gap-4">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <div>
                  <p className="font-semibold text-foreground">{tx("Waiting for WhatsApp")}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{tx("We will update this page automatically.")}</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-5">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <ShieldCheck className="h-7 w-7" />
                </div>
                <div className="w-full rounded-2xl border border-border bg-muted/50 px-4 py-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{tx("WhatsApp number")}</p>
                  <p className="mt-2 text-base font-semibold text-foreground">{selectedPhone}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{tx("Enter this inside WhatsApp")}</p>
                  <div className="mt-2 rounded-2xl border border-border bg-background px-6 py-4 font-mono text-3xl tracking-[0.35em] text-foreground">
                    {pairingCode}
                  </div>
                </div>
                <div className="grid w-full gap-2 text-left text-sm text-muted-foreground">
                  <div className="rounded-2xl border border-border/70 bg-muted/40 px-3 py-3">
                    {tx("1. Open")} <span className="font-semibold text-foreground">WhatsApp</span>
                  </div>
                  <div className="rounded-2xl border border-border/70 bg-muted/40 px-3 py-3">
                    {tx("2. Open")} <span className="font-semibold text-foreground">{tx("Linked Devices")}</span>
                  </div>
                  <div className="rounded-2xl border border-border/70 bg-muted/40 px-3 py-3">
                    {tx("3. Tap")} <span className="font-semibold text-foreground">{tx("Link with phone number instead")}</span>
                  </div>
                  <div className="rounded-2xl border border-border/70 bg-muted/40 px-3 py-3">
                    {tx("4. Enter")} <span className="font-semibold text-foreground">{pairingCode}</span>
                  </div>
                </div>
                <Button
                  className="h-12 w-full rounded-xl"
                  onClick={() => setWaitingForConfirmation(true)}
                >
                  {tx("I entered the code")}
                </Button>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
